/**
 * sz-activity-form — 活动匿名表单 API（通用）
 *
 * 1. POST /app-api/activity/form/auth — 邮箱 / 店铺校验（两步表单 Step1）
 * 2. POST /app-api/activity/form/file/presigned-url/batch — 创建活动表单文件预签名 URL（批量）
 * 3a. PUT  uploadUrl — 直传阿里云 OSS（二进制 PUT，需 OSS 桶配置 CORS）
 * 3b. POST upload_proxy_path — 浏览器 CORS 失败时，由服务端代传 OSS
 * 4. POST /app-api/activity/form/submit — 提交活动表单
 *
 * activityCode、fieldKey、formValues 等由各 section settings 注入，本文件仅封装请求。
 */

(function (global) {
  'use strict';

  /** 邮箱 / 店铺校验 */
  var AUTH_PATH = '/app-api/activity/form/auth';

  /** 批量获取预签名上传 URL */
  var PRESIGNED_BATCH_PATH = '/app-api/activity/form/file/presigned-url/batch';

  /** 提交活动表单 */
  var SUBMIT_PATH = '/app-api/activity/form/submit';

  /** 服务端代传 OSS（绕过浏览器 CORS） */
  var UPLOAD_PROXY_PATH = '/app-api/activity/form/file/upload-proxy';

  /**
   * 预签名请求文件项 — AppActivityFormFilePresignedUrlBatchReqFile
   * @typedef {Object} PresignedUrlBatchFileReq
   * @property {string} name - 【必填】原始文件名，如 a.png
   * @property {string} type - 【必填】MIME 类型，如 image/png
   * @property {number} size - 【必填】文件大小（字节），integer(int64)
   */

  /**
   * 预签名请求体 — appActivityFormFilePresignedUrlBatchReqVO
   * @typedef {Object} PresignedUrlBatchReq
   * @property {string} activityCode - 【必填】活动编码，如 summer-campaign
   * @property {string} fieldKey - 【必填】表单字段 key，如 photos
   * @property {PresignedUrlBatchFileReq[]} files - 【必填】待上传文件列表
   * @property {string} [captchaToken] - 验证码 / 行为验证 token
   */

  /**
   * 预签名响应文件项 — AppActivityFormFilePresignedUrlBatchRespFile
   * @typedef {Object} PresignedUrlBatchFileResp
   * @property {string} clientFileName - 客户端文件名
   * @property {string} uploadToken - 匿名上传令牌，如 anon_upload_xxx；提交表单时填入 attachmentUploadTokens
   * @property {string} uploadUrl - 预签名 OSS PUT URL（阿里云 OSS，curl -X PUT --data-binary）
   */

  /**
   * 预签名响应 data — AppActivityFormFilePresignedUrlBatchRespVO
   * @typedef {Object} PresignedUrlBatchData
   * @property {number} expireSeconds - 上传 URL 过期秒数，integer(int32)，示例 600
   * @property {PresignedUrlBatchFileResp[]} files - 预签名文件结果列表
   */

  /**
   * 预签名响应 — CommonResultAppActivityFormFilePresignedUrlBatchRespVO，HTTP 200 OK
   * @typedef {Object} PresignedUrlBatchEnvelope
   * @property {number} code - integer(int32)，0 表示成功
   * @property {string} msg
   * @property {PresignedUrlBatchData} [data]
   */

  /**
   * POST /app-api/activity/form/file/presigned-url/batch
   * Content-Type: application/json
   *
   * Header（均可选）：
   * - tenant-id: integer(int32)，如 1
   * - Authorization: string，如 Bearer test1
   *
   * Body 示例：
   * {
   *   "activityCode": "summer-campaign",
   *   "fieldKey": "photos",
   *   "files": [
   *     { "name": "a.png", "type": "image/png", "size": 102400 }
   *   ],
   *   "captchaToken": ""
   * }
   *
   * 响应示例（200 OK）：
   * {
   *   "code": 0,
   *   "msg": "",
   *   "data": {
   *     "expireSeconds": 600,
   *     "files": [
   *       {
   *         "clientFileName": "a.png",
   *         "uploadToken": "anon_upload_xxx",
   *         "uploadUrl": "https://polymer-file.oss-cn-hangzhou.aliyuncs.com/..."
   *       }
   *     ]
   *   }
   * }
   */

  /**
   * @typedef {Object.<string, *>} ActivityFormValues
   * 动态表单字段值，key 由后端表单配置 + section settings 决定。
   */

  /**
   * 提交活动表单请求体 — appActivityFormSubmitReqVO
   * @typedef {Object} ActivityFormSubmitReq
   * @property {string} activityCode - 【必填】活动编码，如 new-year
   * @property {string} email - 【必填】邮箱，string(email)
   * @property {number} [storeId] - 店铺 ID，integer(int64)，如 1024
   * @property {string} [storeCode] - 店铺编码，如 main
   * @property {ActivityFormValues} [formValues] - 动态表单字段值 object
   * @property {string[]} [attachmentUploadTokens] - 匿名上传 token 列表；提交成功后后端将 token 绑定到 OSS 图片 URL
   */

  /**
   * 提交活动表单响应 — CommonResultVoid，HTTP 200 OK
   * @typedef {Object} ActivityFormSubmitEnvelope
   * @property {number} code - integer(int32)，0 表示成功
   * @property {string} msg
   * @property {Record<string, never>} [data] - 空对象
   */

  /**
   * POST /app-api/activity/form/submit
   * Content-Type: application/json
   *
   * Header（均可选）：
   * - tenant-id: integer(int32)，如 1
   * - Authorization: string，如 Bearer test1
   *
   * Body 示例：
   * {
   *   "activityCode": "new-year",
   *   "storeId": 1024,
   *   "storeCode": "main",
   *   "email": "demo@example.com",
   *   "formValues": {},
   *   "attachmentUploadTokens": []
   * }
   *
   * attachmentUploadTokens 填入 presigned-url/batch 返回的 uploadToken（OSS PUT 成功后）。
   *
   * 响应示例（200 OK）：
   * { "code": 0, "msg": "", "data": {} }
   */

  function normalizeBase(base) {
    if (!base || typeof base !== 'string') return '';
    return base.replace(/\/+$/, '');
  }

  /**
   * @param {string} baseUrl - API 根地址（https）或同源代理前缀
   * @returns {string}
   */
  function buildApiUrl(baseUrl, path) {
    var base = normalizeBase(baseUrl);
    var combined = base + path;
    if (/^https?:\/\//i.test(combined)) return combined;
    var origin =
      typeof global.location !== 'undefined' && global.location.origin ? global.location.origin : '';
    if (!origin) throw new Error('SZ_ACTIVITY_FORM_API: use absolute https API URL');
    var rel = combined.startsWith('/') ? combined : '/' + combined;
    return new URL(rel, origin).toString();
  }

  /**
   * @param {string} baseUrl
   * @returns {string}
   */
  function buildAuthUrl(baseUrl) {
    return buildApiUrl(baseUrl, AUTH_PATH);
  }

  /**
   * @param {string} baseUrl
   * @returns {string}
   */
  function buildPresignedBatchUrl(baseUrl) {
    return buildApiUrl(baseUrl, PRESIGNED_BATCH_PATH);
  }

  /**
   * @param {string} baseUrl
   * @returns {string}
   */
  function buildSubmitUrl(baseUrl) {
    return buildApiUrl(baseUrl, SUBMIT_PATH);
  }

  /**
   * POST /app-api/activity/form/auth
   * Body: { activityCode, storeCode, email }
   *
   * @param {string} baseUrl
   * @param {{ activityCode: string, storeCode?: string, storeId?: number, email: string }} body
   * @param {{ tenantId?: number|string, authorization?: string }} [headers]
   * @returns {Promise<{ httpOk: boolean, status: number, body: { code?: number, msg?: string, data?: * } }>}
   */
  function authActivityForm(baseUrl, body, headers) {
    return fetch(buildAuthUrl(baseUrl), {
      method: 'POST',
      headers: buildJsonHeaders(headers),
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        return { httpOk: res.ok, status: res.status, body: json };
      });
    });
  }

  /**
   * @param {{ code?: number }|{ body?: { code?: number } }} result
   * @returns {boolean}
   */
  function isAuthSuccess(result) {
    var envelope = result && result.body ? result.body : result;
    return !!(envelope && envelope.code === 0);
  }

  /**
   * @param {{ tenantId?: number|string, authorization?: string }} [headers]
   * @returns {Record<string, string>}
   */
  function buildJsonHeaders(headers) {
    var reqHeaders = { 'Content-Type': 'application/json' };
    if (headers && headers.tenantId != null && headers.tenantId !== '') {
      reqHeaders['tenant-id'] = String(headers.tenantId);
    }
    if (headers && headers.authorization) {
      reqHeaders['Authorization'] = headers.authorization;
    }
    return reqHeaders;
  }

  /**
   * 步骤 1：向后端申请预签名 URL
   *
   * @param {string} baseUrl
   * @param {PresignedUrlBatchReq} body
   * @param {{ tenantId?: number|string, authorization?: string }} [headers]
   * @returns {Promise<{ httpOk: boolean, status: number, body: PresignedUrlBatchEnvelope }>}
   */
  function requestPresignedUrls(baseUrl, body, headers) {
    return fetch(buildPresignedBatchUrl(baseUrl), {
      method: 'POST',
      headers: buildJsonHeaders(headers),
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        return { httpOk: res.ok, status: res.status, body: json };
      });
    });
  }

  /**
   * @param {PresignedUrlBatchEnvelope|{ body?: PresignedUrlBatchEnvelope }} result
   * @returns {boolean}
   */
  function isPresignedSuccess(result) {
    var envelope = result && result.body ? result.body : result;
    return !!(envelope && envelope.code === 0 && envelope.data && envelope.data.files);
  }

  /**
   * 步骤 3：提交活动表单
   *
   * @param {string} baseUrl
   * @param {ActivityFormSubmitReq} body
   * @param {{ tenantId?: number|string, authorization?: string }} [headers]
   * @returns {Promise<{ httpOk: boolean, status: number, body: ActivityFormSubmitEnvelope }>}
   */
  function submitActivityForm(baseUrl, body, headers) {
    return fetch(buildSubmitUrl(baseUrl), {
      method: 'POST',
      headers: buildJsonHeaders(headers),
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        return { httpOk: res.ok, status: res.status, body: json };
      });
    });
  }

  /**
   * @param {ActivityFormSubmitEnvelope|{ body?: ActivityFormSubmitEnvelope }} result
   * @returns {boolean}
   */
  function isSubmitSuccess(result) {
    var envelope = result && result.body ? result.body : result;
    return !!(envelope && envelope.code === 0);
  }

  var MIME_BY_EXT = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    gif: 'image/gif'
  };

  /**
   * 解析文件 MIME，申请预签名与 OSS PUT 必须使用同一 type。
   *
   * @param {File|Blob} file
   * @param {string} [fallback]
   * @returns {string}
   */
  function resolveFileMimeType(file, fallback) {
    if (file && file.type) return file.type;
    var name = file && file.name ? String(file.name) : '';
    var parts = name.split('.');
    if (parts.length > 1) {
      var ext = parts.pop().toLowerCase();
      if (MIME_BY_EXT[ext]) return MIME_BY_EXT[ext];
    }
    return fallback || 'application/octet-stream';
  }

  /**
   * @param {PresignedUrlBatchData|{ files?: PresignedUrlBatchFileResp[] }} data
   * @returns {PresignedUrlBatchFileResp}
   */
  function normalizePresignedFileItem(item) {
    var uploadUrl = item.uploadUrl || item.upload_url;
    var uploadToken = item.uploadToken || item.upload_token;
    if (!uploadUrl || typeof uploadUrl !== 'string') {
      throw new Error('SZ_ACTIVITY_FORM_API: presigned response missing uploadUrl');
    }
    return {
      clientFileName: item.clientFileName || item.client_file_name || '',
      uploadToken: uploadToken || '',
      uploadUrl: uploadUrl.trim()
    };
  }

  /**
   * @param {PresignedUrlBatchData|{ files?: PresignedUrlBatchFileResp[] }} data
   * @returns {PresignedUrlBatchFileResp[]}
   */
  function pickPresignedFileItems(data) {
    var files = data && data.files;
    if (!files || !files.length) {
      throw new Error('SZ_ACTIVITY_FORM_API: presigned response has no files');
    }
    return files.map(normalizePresignedFileItem);
  }

  /**
   * @param {PresignedUrlBatchData|{ files?: PresignedUrlBatchFileResp[] }} data
   * @returns {PresignedUrlBatchFileResp}
   */
  function pickPresignedFileItem(data) {
    return pickPresignedFileItems(data)[0];
  }

  /**
   * @param {Error|string|null|undefined} err
   * @returns {boolean}
   */
  function isUploadNetworkError(err) {
    var msg = err && err.message ? String(err.message) : String(err || '');
    msg = msg.toLowerCase();
    return msg.indexOf('cors') !== -1
      || msg.indexOf('network') !== -1
      || msg.indexOf('blocked') !== -1
      || msg.indexOf('status 0') !== -1;
  }

  /**
   * 步骤 2a：使用预签名 URL 直传 OSS（PUT，等价 curl --data-binary）
   *
   * @param {string} uploadUrl - data.files[n].uploadUrl
   * @param {File|Blob} file
   * @param {string} mimeType
   * @returns {Promise<{ ok: boolean, status: number }>}
   */
  function uploadFileToOss(uploadUrl, file, mimeType) {
    var contentType = mimeType || resolveFileMimeType(file);
    var source = file && typeof file.arrayBuffer === 'function'
      ? file.arrayBuffer()
      : Promise.resolve(file);

    return source.then(function(buffer) {
      return new Promise(function(resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ ok: true, status: xhr.status });
            return;
          }
          reject(new Error('OSS upload failed (' + xhr.status + ')'));
        };
        xhr.onerror = function() {
          var hint = xhr.status === 0
            ? 'OSS upload blocked by CORS'
            : 'OSS upload network error';
          reject(new Error(hint));
        };
        xhr.send(buffer);
      });
    });
  }

  /**
   * @param {string} baseUrl
   * @param {string} proxyPath
   * @returns {string}
   */
  function buildUploadProxyUrl(baseUrl, proxyPath) {
    var path = proxyPath || UPLOAD_PROXY_PATH;
    return buildApiUrl(baseUrl, path);
  }

  /**
   * 步骤 2b：经业务 API 代传 OSS（multipart POST，服务端执行 PUT）
   *
   * @param {string} baseUrl
   * @param {string} proxyPath
   * @param {string} uploadUrl
   * @param {File|Blob} file
   * @param {string} mimeType
   * @param {{ tenantId?: number|string, authorization?: string }} [headers]
   * @returns {Promise<{ ok: boolean, status: number }>}
   */
  function uploadFileViaProxy(baseUrl, proxyPath, uploadUrl, file, mimeType, headers) {
    var contentType = mimeType || resolveFileMimeType(file);
    var formData = new FormData();
    formData.append('file', file, file && file.name ? file.name : 'upload');
    formData.append('uploadUrl', uploadUrl);
    formData.append('contentType', contentType);

    var reqHeaders = {};
    if (headers && headers.tenantId != null && headers.tenantId !== '') {
      reqHeaders['tenant-id'] = String(headers.tenantId);
    }
    if (headers && headers.authorization) {
      reqHeaders['Authorization'] = headers.authorization;
    }

    return fetch(buildUploadProxyUrl(baseUrl, proxyPath), {
      method: 'POST',
      headers: reqHeaders,
      body: formData
    }).then(function(res) {
      return res.text().then(function(text) {
        /** @type {{ code?: number, msg?: string }} */
        var body = null;
        try {
          body = text ? JSON.parse(text) : null;
        } catch (e) {
          body = null;
        }
        if (res.ok && body && body.code === 0) {
          return { ok: true, status: res.status };
        }
        var msg = body && body.msg ? body.msg : 'Proxy upload failed (' + res.status + ')';
        throw new Error(msg);
      });
    });
  }

  /**
   * @param {{
   *   baseUrl?: string,
   *   proxyPath?: string,
   *   uploadUrl: string,
   *   file: File|Blob,
   *   mimeType?: string,
   *   headers?: { tenantId?: number|string, authorization?: string },
   *   mode?: 'auto'|'direct'|'proxy'
   * }} options
   * @returns {Promise<{ ok: boolean, status: number }>}
   */
  function uploadActivityFile(options) {
    var baseUrl = options.baseUrl || '';
    var proxyPath = options.proxyPath || UPLOAD_PROXY_PATH;
    var uploadUrl = options.uploadUrl;
    var file = options.file;
    var mimeType = options.mimeType;
    var headers = options.headers;
    var mode = options.mode || 'direct';

    function tryDirect() {
      return uploadFileToOss(uploadUrl, file, mimeType);
    }

    function tryProxy() {
      if (!baseUrl) {
        return Promise.reject(new Error('Upload proxy requires api_base_url'));
      }
      return uploadFileViaProxy(baseUrl, proxyPath, uploadUrl, file, mimeType, headers);
    }

    if (mode === 'proxy') return tryProxy();
    if (mode === 'direct') return tryDirect();

    return tryDirect().catch(function(err) {
      if (!isUploadNetworkError(err) || !baseUrl) throw err;
      return tryProxy();
    });
  }

  global.SZ_ACTIVITY_FORM_API = {
    AUTH_PATH: AUTH_PATH,
    PRESIGNED_BATCH_PATH: PRESIGNED_BATCH_PATH,
    SUBMIT_PATH: SUBMIT_PATH,
    UPLOAD_PROXY_PATH: UPLOAD_PROXY_PATH,
    buildAuthUrl: buildAuthUrl,
    buildPresignedBatchUrl: buildPresignedBatchUrl,
    buildSubmitUrl: buildSubmitUrl,
    authActivityForm: authActivityForm,
    isAuthSuccess: isAuthSuccess,
    requestPresignedUrls: requestPresignedUrls,
    isPresignedSuccess: isPresignedSuccess,
    resolveFileMimeType: resolveFileMimeType,
    pickPresignedFileItem: pickPresignedFileItem,
    pickPresignedFileItems: pickPresignedFileItems,
    isUploadNetworkError: isUploadNetworkError,
    buildUploadProxyUrl: buildUploadProxyUrl,
    uploadFileToOss: uploadFileToOss,
    uploadFileViaProxy: uploadFileViaProxy,
    uploadActivityFile: uploadActivityFile,
    submitActivityForm: submitActivityForm,
    isSubmitSuccess: isSubmitSuccess
  };
})(typeof window !== 'undefined' ? window : globalThis);
